import React, { useState } from "react";

import { faTrash } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnApi, GridApi } from "ag-grid-community";
import { sortBy } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";
import { v4 as uuidv4 } from "uuid";

import { SavedViewAction, setTableView } from "actions";

import { ConfirmDialog, createModalTitle } from "components/ConfirmDialog";
import { Button } from "components/Form";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import { Paper } from "components/Paper";
import { ContentTypeNames } from "components/UserRoles/constants";

import { OwnerTypeLabels, OwnerTypes } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import {
  DeploymentPermissions,
  LivestockAgentPermissions,
  SaleyardAdminPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { userTypes } from "constants/users";

import { ForStaff } from "containers/ForUserType";

import { hasPermission } from "lib/permissions";
import { sortSavedViewCategories } from "lib/savedViews";
import toast from "lib/toast";

import {
  getActiveLivestockAgentDeployment,
  getActiveRole,
  getActiveSaleyardAdminSaleyard,
  getCategorisedSavedViewsByTableName,
  getTableSetting,
} from "selectors";

import { useToggle } from "hooks";

import { SavedViewsDialog } from "./SavedViewsDialog";

interface SavedViewClickerProps {
  active?: boolean;
  width?: string;
}

const SavedViewClicker = styled.button<SavedViewClickerProps>(
  ({ theme, active, width = "auto" }) => `
  padding: ${theme.space[1]}px;
  background: ${active ? theme.colors.primaryActive : "inherit"};
  color: ${active ? theme.colors.white : "inherit"};
  box-shadow: 0 0 0 1px ${theme.colors.controlBorder};
  text-align: center;
  cursor: pointer;
  &:hover {
    color: ${theme.colors.white};
    background-color: ${theme.colors.primaryHighlight};
    box-shadow: 0 0 0 1px ${theme.colors.primaryHighlight};
  }
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${width};
	border: none;
	outline: inherit;
`,
);

interface ViewCategoryListProps {
  heading: string;
  views?: SavedView[];
  handleApplyView: (view: SavedView) => void;
  handleDelete?: ((id: string) => void) | null;
  currentViewId: string | null;
}

const ViewCategoryList = ({
  heading,
  views = [],
  handleApplyView,
  handleDelete = null,
  currentViewId,
}: ViewCategoryListProps) => {
  if (views.length === 0) {
    return null;
  }

  return (
    <Column>
      <h2>{heading}</h2>
      {sortBy(views, "name").map(view => (
        <Row key={view.id}>
          <SavedViewClicker
            onClick={() => handleApplyView(view)}
            width="100%"
            active={view.id === currentViewId}
            type="button"
          >
            {view.name}
          </SavedViewClicker>
          {handleDelete && (
            <SavedViewClicker
              onClick={() => handleDelete(view.id)}
              type="button"
            >
              <FontAwesomeIcon icon={faTrash} />
            </SavedViewClicker>
          )}
        </Row>
      ))}
    </Column>
  );
};

interface UserTypeConfig {
  canCreateSelf: boolean;
  canCreateSaleyard: boolean;
  canCreateDeployment: boolean;
  shareableLabel: string;
}

const userTypeConfigMap = (
  userRole: ActiveRole,
  saleyard: Saleyard,
  deployment: Deployment,
): Partial<UserTypeConfig> => {
  // Not all userRoles currently have permissions returned.  This should be simplified
  // when it is available.
  if (userRole.type === userTypes.SALEYARD_ADMIN) {
    return {
      canCreateSelf: hasPermission(
        userRole,
        SaleyardAdminPermissions.canAddSavedView,
      ),
      canCreateSaleyard: hasPermission(
        saleyard,
        SaleyardPermissions.canAddSavedView,
      ),
      canCreateDeployment: false,
      shareableLabel: saleyard.name,
    };
  }

  if (userRole.type === userTypes.LIVESTOCK_AGENT) {
    return {
      canCreateSelf: hasPermission(
        userRole,
        LivestockAgentPermissions.canAddSavedView,
      ),
      canCreateSaleyard: false,
      canCreateDeployment: hasPermission(
        deployment,
        DeploymentPermissions.canAddSavedView,
      ),
      shareableLabel: deployment.name,
    };
  }

  return {};
};

interface SavedViewFormValues {
  name: string;
  ownerType: string;
}

interface SavedViewsPanelProps {
  api: GridApi;
  // columnApi is injected by AgGrid v29 into custom tool panels.
  // In v33 it is removed — migrate to api.getColumnState() in AG-5301.
  columnApi: ColumnApi;
  tableName: string;
}

const SavedViewsPanel = ({
  api,
  columnApi,
  tableName,
}: SavedViewsPanelProps) => {
  const [isSaveDialogOpen, toggleIsSaveDialogOpen] = useToggle(false);
  const [confirmDeleteSavedView, setConfirmDeleteSavedView] = useState<
    string | null
  >(null);

  const activeRole = useSelector(getActiveRole);
  const tableState = useSelector(getTableSetting(tableName));
  const currentViewId = tableState?.viewId;
  const categorisedSavedViews = useSelector(
    getCategorisedSavedViewsByTableName(tableName),
  );

  const dispatch = useDispatch();

  const applySavedView = ({ id }: SavedView) => {
    dispatch(setTableView(tableName, id));
  };

  const userRole = useSelector(getActiveRole);
  const currentSaleyard = useSelector(getActiveSaleyardAdminSaleyard);
  const activeDeployment = useSelector(getActiveLivestockAgentDeployment);

  const deleteSavedView = (id: string) => {
    dispatch(SavedViewAction.delete(id));
    setConfirmDeleteSavedView(null);
  };

  const save = (values: SavedViewFormValues) => {
    let ownerId = null;
    let { ownerType } = values;
    if (ownerType === OwnerTypes.DEPLOYMENT) {
      ownerId = activeDeployment.id;
    } else if (ownerType === OwnerTypes.SALEYARD) {
      ownerId = currentSaleyard.id;
    } else {
      // Assume the owner is a userrole
      ownerType = ContentTypeNames[activeRole.type];
      ownerId = activeRole.id;
    }

    dispatch(
      SavedViewAction.create({
        name: values.name,
        content: JSON.stringify(columnApi.getColumnState()),
        filters: api.getFilterModel(),
        table: tableName,
        ownerId,
        ownerType,
        id: uuidv4(),
      }),
    );
    toggleIsSaveDialogOpen();
  };

  const configMap = userTypeConfigMap(
    userRole,
    currentSaleyard,
    activeDeployment,
  );

  const permissionToCreate = configMap
    ? configMap.canCreateDeployment ||
      configMap.canCreateSaleyard ||
      configMap.canCreateSelf
    : false;

  const copyColumnsToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(columnApi.getColumnState()));
    toast.success("Columns copied to clipboard.");
  };

  const copyFiltersToClipboard = () => {
    navigator.clipboard.writeText(JSON.stringify(api.getFilterModel()));
    toast.success("Filters copied to clipboard.");
  };

  return (
    <>
      <Paper>
        <ForStaff>
          <Button type="button" onClick={copyColumnsToClipboard}>
            {`Copy "${tableName}" Columns`}
          </Button>
          <Button type="button" onClick={copyFiltersToClipboard}>
            {`Copy "${tableName}" Filters`}
          </Button>
        </ForStaff>
        <Button
          type="button"
          onClick={toggleIsSaveDialogOpen}
          disabled={!permissionToCreate}
        >
          Save Current View
        </Button>

        {Object.keys(categorisedSavedViews)
          .sort(sortSavedViewCategories)
          .map(categoryLabel => (
            <ViewCategoryList
              key={categoryLabel}
              heading={categoryLabel}
              views={categorisedSavedViews[categoryLabel]}
              handleApplyView={applySavedView}
              handleDelete={
                categoryLabel === OwnerTypeLabels[OwnerTypes.GLOBAL]
                  ? undefined
                  : setConfirmDeleteSavedView
              }
              currentViewId={currentViewId}
            />
          ))}
      </Paper>

      {isSaveDialogOpen && (
        <SavedViewsDialog
          handleSave={save}
          handleClose={toggleIsSaveDialogOpen}
          configMap={configMap}
        />
      )}
      {confirmDeleteSavedView && (
        <ConfirmDialog
          title={createModalTitle("this View")}
          isOpen
          onCancel={() => setConfirmDeleteSavedView(null)}
          onDelete={() => deleteSavedView(confirmDeleteSavedView)}
        />
      )}
    </>
  );
};

// AgGrid injects api and columnApi automatically; tableName comes from toolPanelParams
const LoadingWrapper = (props: SavedViewsPanelProps) => (
  <WaitForSync requiredData={[ApiModel.DEPLOYMENTS, ApiModel.SALEYARDS]}>
    <SavedViewsPanel {...props} />
  </WaitForSync>
);

export default LoadingWrapper;
